import { set_token, set_user_details } from '@/router';

const login_url = process.env.VUE_APP_LOGIN_URL;
// otp verify url
const otp_verify_url = process.env.VUE_APP_OTP_VERIFY_URL;

export default {
  name: 'OneTimePin',

  data() {
    return ({
      otp: ['', '', '', '', '', ''],
      timer: 300,
      email: sessionStorage.getItem('pending_username') || '',
      timer_interval: null,
      error_message: '',
      success_message: 'Verification code has been sent to your email.',
      otp_verify_url: otp_verify_url,
      login_url: login_url
    });
  },

  computed: {
    formatted_timer() {
      const minutes = Math.floor(this.timer / 60);
      const seconds = this.timer % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  },

  mounted() {
    const pending_username = sessionStorage.getItem('pending_username');

    if (!pending_username) {
      this.$router.push('/login');
      return;
    }

    this.email = pending_username;
    this.start_timer();
  },

  beforeUnmount() {
    this.clear_timer();
  },

  methods: {
    clear_pending_login() {
      sessionStorage.removeItem('pending_user_data');
      sessionStorage.removeItem('pending_username');
      sessionStorage.removeItem('pending_challenge_token');
      sessionStorage.removeItem('pending_login_password');
    },

    start_timer() {
      this.clear_timer();
      this.timer = 300;

      this.timer_interval = setInterval(() => {
        if (this.timer > 0) {
          this.timer -= 1;
        } else {
          this.clear_timer();
        }
      }, 1000);
    },

    clear_timer() {
      if (this.timer_interval) {
        clearInterval(this.timer_interval);
        this.timer_interval = null;
      }
    },

    handle_input(index, event) {
      const value = (event.target.value || '').replace(/\D/g, '');

      if (value.length > 1) {
        this.apply_otp_value(value, parseInt(index, 10), event.target);
        return;
      }

      this.otp[parseInt(index, 10)] = value;

      // move focus forward
      if (value && index < this.otp.length - 1) {
        const inputs = event.target.closest('.otp-inputs').querySelectorAll('input');
        if (inputs[index + 1]) {
          inputs[index + 1].focus();
        }
      }

      // auto submit when full
      const otp_code = this.get_otp_code();
      if (otp_code.length === 6) {
        this.handle_verify_otp();
      }
    },

    apply_otp_value(raw_value, start_index = 0, active_input = null) {
      const digits = String(raw_value || '').replace(/\D/g, '').slice(0, this.otp.length);

      if (!digits) {
        return;
      }

      const next_otp = [...this.otp];

      digits.split('').forEach((digit, offset) => {
        const target_index = start_index + offset;
        if (target_index < next_otp.length) {
          next_otp[parseInt(target_index, 10)] = digit;
        }
      });

      this.otp = next_otp;

      this.$nextTick(() => {
        const inputs = active_input
          ? active_input.closest('.otp-inputs')?.querySelectorAll('input')
          : null;

        if (!inputs || !inputs.length) {
          return;
        }

        const filled_index = Math.min(start_index + digits.length - 1, this.otp.length - 1);
        const next_empty_index = this.otp.findIndex(digit => !digit);
        const focus_index = next_empty_index === -1 ? filled_index : next_empty_index;

        if (inputs[parseInt(focus_index, 10)]) {
          inputs[parseInt(focus_index, 10)].focus();
        }

        if (this.get_otp_code().length === this.otp.length) {
          this.handle_verify_otp();
        }
      });
    },

    handle_paste(index, event) {
      const pasted_text = event.clipboardData?.getData('text') || '';
      const digits = pasted_text.replace(/\D/g, '');

      if (!digits) {
        return;
      }

      event.preventDefault();
      this.apply_otp_value(digits, parseInt(index, 10), event.target);
    },

    handle_backspace(index, event) {
      if (!this.otp[parseInt(index, 10)] && index > 0) {
        const inputs = event.target.closest('.otp-inputs').querySelectorAll('input');
        if (inputs[index - 1]) {
          inputs[index - 1].focus();
        }
      }
    },

    async resend_otp() {
      const pending_password = sessionStorage.getItem('pending_login_password');

      if (!this.login_url || !this.email || !pending_password) {
        this.error_message = 'Session expired. Please log in again.';
        this.clear_pending_login();
        return;
      }

      try {
        const response = await fetch(this.login_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            username: this.email,
            password: pending_password,
            client_app: 'dws'
          })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status !== 'success' || !data.challenge_token) {
          throw new Error(data.error || data.message || 'Failed to resend OTP.');
        }

        sessionStorage.setItem('pending_challenge_token', data.challenge_token);
        sessionStorage.setItem('pending_user_data', JSON.stringify(data.user || {}));
        this.start_timer();
        this.success_message = 'A new verification code has been sent.';
        this.error_message = '';
      } catch (error) {
        this.error_message = 'Failed to resend OTP. Please try again.';
      }
    },

    // Keep the current template hook working.
    resendOTP() {
      return this.resend_otp();
    },

    get_otp_code() {
      return this.otp.join('').trim();
    },

    async handle_verify_otp() {
      this.error_message = '';

      const otp_code = this.get_otp_code();

      if (otp_code.length !== 6) {
        this.error_message = 'Please enter the full 6-digit code';
        return;
      }

      const pending_username = sessionStorage.getItem('pending_username');
      const pending_challenge_token = sessionStorage.getItem('pending_challenge_token');

      if (!pending_username || !pending_challenge_token) {
        this.error_message = 'Session expired. Please log in again.';
        return;
      }

      try {
        const response = await fetch(this.otp_verify_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            challenge_token: pending_challenge_token,
            token: otp_code
          })
        });

        if (!response.ok) {
          throw new Error('Invalid or expired verification code.');
        }

        const data = await response.json().catch(() => ({}));
        if (!data.token) {
          throw new Error('No authentication token received.');
        }

        // success flow
        const pending_user_data_str = sessionStorage.getItem('pending_user_data');
        const pending_user = pending_user_data_str ? JSON.parse(pending_user_data_str) : {};
        const user = data.user || pending_user;

        const role_name = user.user_role && user.user_role.role
          ? user.user_role.role.name
          : null;

        set_token(data.token);
        set_user_details({
          role_type: role_name,
          user_id: user.id,
          first_name: user.first_name || '',
          last_name: user.last_name || ''
        });

        // clean session
        this.clear_pending_login();

          setTimeout(() => {
          this.$router.push('/home');
        }, 500);
      } catch (error) {
        this.error_message = error.message || 'Invalid OTP. Please try again.';
        this.otp = ['', '', '', '', '', ''];
      }
    },

    // Keep the current template hook working.
    submit_otp() {
      return this.handle_verify_otp();
    },

    cancel_verification() {
      this.clear_pending_login();

      this.$router.push('/login');
    }
  }
};
